<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <section class="content">
      <div class="container-fluid">
        <b-tabs content-class="mt-3">
          <b-tab title="Players" active>
            <b-row class="col-lg-12">
              <div class="col-lg-4 col-md-4 col-sm-12">
                <b-form-group
                  label-cols-lg="3"
                  label-for="filter-uid"
                  label="User ID: "
                >
                  <b-form-input
                    id="filter-uid"
                    type="search"
                    v-model="filters.playerId"
                    autocomplete="off"
                    placeholder="User ID"
                    trim
                    @keyup.enter="loadItems"
                  >
                  </b-form-input>
                </b-form-group>
              </div>
              <div class="col-lg-4 col-md-4 col-sm-12">
                <b-form-group
                  label-cols-lg="3"
                  label-for="filter-operator"
                  label="Operator: "
                >
                  <multiselect
                    id="filter-operator"
                    v-model="filters.partnerId"
                    :options="operatorOptions"
                    label="label"
                    track-by="value"
                    :multiple="false"
                    :placeholder="$t('Select')"
                    :select-label="$t('Press enter to select')"
                    :deselect-label="$t('Press enter to remove')"
                    :selected-label="$t('Selected')"
                    :disabled="!operatorsLoaded"
                    :allow-empty="true"
                  ></multiselect>
                </b-form-group>
              </div>
            </b-row>

            <b-row class="col-lg-12">
              <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
                <filter-buttons
                  :cancel="this.cancel"
                  :loadFn="loadItems"
                  :cancelFn="cancelRequest"
                  :resetFn="clearFilters"
                  :load-button-disabled="!hasFilters"
                />
              </div>
            </b-row>

            <div class="row col-12" id="loading-container">
              <div class="col-12 mb-10">
                <div class="mb-10">
                  <div class="table-wrapper mb-10">
                    <b-table
                      :striped="true"
                      :small="true"
                      :hover="true"
                      :empty-text="emptyText"
                      :sort-by="sortBy"
                      :sort-desc="sortDesc"
                      show-empty
                      :busy="!!cancel"
                      :items="items"
                      :fields="fields"
                      :no-local-sorting="true"
                      :sort-icon-left="true"
                      @sort-changed="sortingChanged"
                    >
                      <template #cell(partnerId)="row">
                        {{ operatorLabel(row.item.partnerId) }}
                      </template>

                      <template #cell(tv)="row">
                        <span
                          class="badge"
                          :class="
                            row.item.tv ? 'badge-success' : 'badge-secondary'
                          "
                        >
                          {{ row.item.tv ? 'Yes' : 'No' }}
                        </span>
                      </template>

                      <template #cell(tvPlayer)="row">
                        <span
                          class="badge"
                          :class="
                            row.item.tvPlayer
                              ? 'badge-success'
                              : 'badge-secondary'
                          "
                        >
                          {{ row.item.tvPlayer ? 'Yes' : 'No' }}
                        </span>
                      </template>

                      <template #cell(actions)="row">
                        <EditForm
                          :default-values="{ ...row.item }"
                          title="Edit player"
                          @finish="handleItemsUpdate"
                        ></EditForm>
                      </template>
                    </b-table>
                  </div>
                </div>

                <div class="row mb-10" v-if="searched">
                  <div class="col-12">
                    <b-pagination
                      class="float-right"
                      v-model="pagination.page"
                      :total-rows="pagination.records"
                      :per-page="pagination.onPage"
                      @change="onPageChange"
                      first-number
                      last-number
                    />
                  </div>
                </div>

                <div class="row mb-10" v-if="searched">
                  <div class="col-12">
                    <ve-pagination
                      class="float-right"
                      :page-index="pagination.page"
                      :page-size="pagination.onPage"
                      :total="pagination.records"
                      :pagingCount="pagination.count"
                      :layout="['total', 'sizer']"
                      :pageSizeOption="pagination.pageSizeOption"
                      @on-page-size-change="onPageSizeChange"
                    />
                  </div>
                </div>
              </div>
            </div>
          </b-tab>

          <b-tab title="Players Chips" lazy>
            <PlayersChipsTab
              :operator-options="operatorOptions"
              :operators-loaded="operatorsLoaded"
            />
          </b-tab>
        </b-tabs>
      </div>
    </section>
  </div>
</template>

<script>
import EditPlayerForm from '../../components/Players/EditPlayerForm'
import PlayersChipsTab from '../../components/Players/PlayersChipsTab'
import axios from 'axios'
import { getRouteIcon, ROUTE_USERS } from '../../utils/routerIcons'
import Multiselect from 'vue-multiselect'
import FilterButtons from '../../components/FilterButtons'

function getDefaultPagination() {
  return {
    onPage: 25,
    page: 1,
    records: 0,
    count: 5,
    pageSizeOption: [25, 50, 100, 200]
  }
}

function getDefaultSort() {
  return {
    column: 'name',
    order: 'ASC'
  }
}

function getDefaultFilters() {
  return {
    playerId: null,
    partnerId: null
  }
}

export default {
  components: {
    EditForm: EditPlayerForm,
    PlayersChipsTab,
    Multiselect,
    FilterButtons
  },
  data() {
    return {
      sortBy: 'name',
      sortDesc: false,
      filters: getDefaultFilters(),
      cancel: null,
      error: null,
      searched: false,
      operatorOptions: [],
      operatorsLoaded: false,
      fields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'playerId', label: 'User ID', sortable: true },
        { key: 'partnerId', label: 'Operator', sortable: false },
        { key: 'tv', label: 'TV', sortable: false },
        { key: 'tvPlayer', label: 'TV Player', sortable: false },
        { key: 'actions', label: 'Actions' }
      ],
      items: [],
      sorts: getDefaultSort(),
      pagination: getDefaultPagination()
    }
  },
  computed: {
    emptyText() {
      if (this.cancel) {
        return 'Loading items..'
      }

      if (this.error) {
        return this.error
      }

      if (!this.searched) {
        return 'Use filters to search players'
      }

      return 'There are no records to show'
    },
    hasFilters() {
      return !!this.filters.playerId || !!this.filters.partnerId?.value
    },
    routeIcon: () => getRouteIcon(ROUTE_USERS)
  },
  methods: {
    operatorLabel(partnerId) {
      if (!partnerId) {
        return ''
      }

      const operator = this.operatorOptions.find(
        operator => operator.value === partnerId
      )

      return operator ? operator.label : partnerId
    },
    loadOperators() {
      return this.$http({
        url: '/api/v1/country-partners-for-select',
        method: 'GET'
      })
        .then(resp => {
          this.operatorOptions = resp.data
          this.operatorsLoaded = true
        })
        .catch(() => {
          this.$toastr.e('Failed to load operators')
        })
    },
    clearFilters() {
      this.filters = getDefaultFilters()
      this.sortBy = getDefaultSort().column
      this.sortDesc = getDefaultSort().order === 'DESC'
      this.pagination = getDefaultPagination()
      this.items = []
      this.searched = false
      this.error = null
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.pagination.page = 1

      if (this.searched) {
        this.loadItems()
      }
    },
    handleItemsUpdate() {
      if (this.searched) {
        this.loadItems()
      }
    },
    loadItems() {
      if (!this.hasFilters) {
        return
      }

      this.items = []
      this.searched = true

      let self = this

      this.$http({
        url: '/api/v1/aggregator-players',
        params: {
          playerId: this.filters.playerId,
          partnerId: this.filters.partnerId?.value,

          // pagination
          limit: this.pagination.onPage,
          offset: this.pagination.onPage * (this.pagination.page - 1),

          // sort params
          orderBy: this.sortBy,
          direction: this.sortDesc ? 'DESC' : 'ASC'
        },
        method: 'GET',
        cancelToken: new axios.CancelToken(c => {
          self.cancel = c
        })
      })
        .then(resp => {
          let {
            items,
            pagination: { limit, totalCount }
          } = resp.data

          this.items = items
          this.pagination.onPage = limit
          this.pagination.records = totalCount
        })
        .catch(err => {
          this.items = []
          this.error = err
        })
        .finally(() => {
          this.cancel = null
        })
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel('this -> cancel previous request')
      }
    },
    onPageChange(page) {
      this.pagination.page = page
      this.loadItems()
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1
      this.pagination.onPage = pageSize
      this.loadItems()
    }
  },
  async beforeMount() {
    await this.loadOperators()
  }
}
</script>
